import { Button, Container, Theme, Typography } from "@mui/material";
import {
  Brand,
  ButtonVariant,
  Domains,
  FlowType,
  HeadingLevel,
  LegalArea,
} from "@/core/types/types";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { ConfigContext } from "@/core/contexts/ConfigContext";
import { useClaimCheck } from "@/core/hooks/useClaimCheck/useClaimCheck";
import { getDomainData } from "@/core/utils/getBrandConfiguration";

const styles = (theme: Theme) => ({
  root: {
    display: "flex",
    gap: theme.spacing(2),
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  buttons: {
    width: "100%",
    maxWidth: "456px",
    textTransform: "inherit",
  },
});

const ERROR_THRESHOLD: number = 3;
const ERROR_PROCESS_DATA: string = "errorProcessData";

interface FallbackUIProps {
  definitionName: string;
}

const FallbackUI = ({ definitionName }: FallbackUIProps) => {
  const classes: any = useCustomStyles(styles);
  const [retryCountState, setRetryCountState] = useState(0);
  const [isClicked, setIsClicked] = useState(false);

  const initializationConfig = useContext(ConfigContext);

  const { flush } = useClaimCheck();

  const currentProcess = Cookies.get("process");
  const initProcessData = {
    processId: currentProcess,
    retryCount: 0,
  };

  const { brandFamily, domain, forceShowBackupCC } = initializationConfig;

  const initStoreData = () => {
    const localStorageItem = localStorage.getItem(ERROR_PROCESS_DATA);
    if (!localStorageItem) {
      localStorage.setItem(
        ERROR_PROCESS_DATA,
        JSON.stringify({
          processId: currentProcess,
          retryCount: 0,
        })
      );
      return;
    }
    setRetryCountState(JSON.parse(localStorageItem).retryCount + 1);
  };

  const getStoredData = () => {
    const localStorageItem = localStorage.getItem(ERROR_PROCESS_DATA);
    if (localStorageItem) {
      const localStorageData = JSON.parse(localStorageItem);
      const isSameProcess = localStorageData.processId === currentProcess;
      return isSameProcess ? localStorageData : initProcessData;
    }
    return initProcessData;
  };

  useEffect(() => {
    initStoreData();
    setIsClicked(false);
  }, []);

  const updateStoredData = () => {
    const { processId, retryCount } = getStoredData();
    setIsClicked(true);
    const isSameProcess = processId === currentProcess;
    isSameProcess ? setRetryCountState(retryCount + 1) : setRetryCountState(0);
    const dataToStore = isSameProcess
      ? { processId, retryCount: retryCount + 1 }
      : initProcessData;
    localStorage.setItem(ERROR_PROCESS_DATA, JSON.stringify(dataToStore));
  };

  const handleReload = (): void => {
    if (typeof window !== "undefined") {
      !isClicked && updateStoredData();
      window.location.reload();
    }
  };

  const handleRestartFlow = (): void => {
    setIsClicked(true);
    localStorage.removeItem(ERROR_PROCESS_DATA);

    flush().then(() => {
      if (typeof window !== "undefined") {
        Cookies.remove("process");
        window.location.reload();
      }
    });
  };

  const handleBackupCCRedirect = (): void => {
    const backupURL =
      getDomainData(brandFamily, LegalArea.DEFAULT, domain ?? Domains.DEFAULT)
        .BackupClaimCheckData?.url ||
      getDomainData(Brand.FLIGHTRIGHT, LegalArea.DEFAULT, Domains.DE)
        .BackupClaimCheckData?.url;

    if (backupURL) {
      localStorage.removeItem(ERROR_PROCESS_DATA);
      window.location.href = backupURL;
    }
  };

  const isRedirectScreen: boolean | undefined =
    (retryCountState >= ERROR_THRESHOLD || forceShowBackupCC) &&
    definitionName !== FlowType.MIE &&
    definitionName !== FlowType.ARB &&
    definitionName !== FlowType.VER;

  return (
    <Container sx={classes?.root}>
      <Typography variant={HeadingLevel.H1}>{t("error.title")}</Typography>
      <Typography variant={HeadingLevel.Body2} fontWeight={400}>
        {isRedirectScreen ? t("error.subtitle.redirect") : t("error.subtitle")}
      </Typography>
      <Typography fontSize={64}>
        {isRedirectScreen ? <>😞</> : <>😔</>}
      </Typography>
      {!isRedirectScreen ? (
        <>
          <Button
            sx={classes?.buttons}
            type="submit"
            onClick={handleReload}
            variant={ButtonVariant.Contained}
          >
            {t("error.btn.reload.label")}
          </Button>
          <Button
            sx={classes?.buttons}
            type="submit"
            onClick={handleRestartFlow}
            variant={ButtonVariant.Outlined}
          >
            {t("error.btn.restart.label")}
          </Button>
        </>
      ) : (
        <>
          <Button
            sx={classes?.buttons}
            type="submit"
            onClick={handleRestartFlow}
            variant={ButtonVariant.Contained}
          >
            {t("error.btn.restart.label")}
          </Button>
          <Button
            sx={classes?.buttons}
            type="submit"
            onClick={handleBackupCCRedirect}
            variant={ButtonVariant.Outlined}
          >
            {t("error.btn.backup.label")}
          </Button>
        </>
      )}
    </Container>
  );
};

export { FallbackUI };
